/* ============================================================
   SOLVEN — theme behaviour for the design prototype markup
   (vanilla JS, no dependencies)
   ============================================================ */
(() => {
  'use strict';

  const S = window.Solven || { routes: {}, strings: {} };
  const body = document.body;
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- money: "35.99 €", ".00" dropped (matches snippets/money.liquid) ---------- */
  function formatMoney(cents) {
    const v = (Number(cents) || 0) / 100;
    const n = v.toFixed(2).replace(/\.00$/, '');
    return (S.currency || 'EUR') === 'EUR' ? `${n} €` : `${S.currencySymbol || '$'}${n}`;
  }

  /* ============================================================
     REVEALS
     The prototype's own .reveal elements, plus data-anim on most
     other blocks (assigned here so templates stay clean).
     ============================================================ */
  const ANIM_MAP = [
    ['.section-head, .m-edit-head', 'up'],
    ['.promise-cell', 'up', 90],
    ['.edit-card, .m-prod-card', 'up', 120],
    ['.bundle-card', 'up', 140],
    ['.story-left', 'left'],
    ['.story-right > p, .story-right > .story-stats', 'up', 70],
    ['.review', 'up', 120],
    ['.faq-item', 'up', 60],
    ['.faq-cta', 'fade'],
    ['.trust-head, .trust-stamps', 'zoom'],
    ['.footer-grid > div', 'up', 90],
    ['.footer-bottom', 'fade'],
    ['.page-hero__media', 'zoom'],
    ['.iwt__media', 'left'],
    ['.iwt__text', 'right'],
    ['.rich-text__inner > *', 'up', 90],
    ['.glossary-card', 'up', 70],
    ['.full-ing__item', 'up', 90],
    ['.contact__info > *', 'left', 80],
    ['.contact__form', 'right'],
    ['.pdp__gallery', 'zoom'],
    ['.pdp__info > *', 'up', 50],
    ['.fbt__item', 'up', 100],
    ['.tile', 'up', 100],
    ['.blog-card', 'up', 100],
    ['.main-cart__inner > *', 'up', 80]
  ];

  // Elements waiting to be revealed. Anything scrolled past (anchor jumps, fast flicks)
  // is revealed on the next scroll frame so nothing stays invisible.
  const pending = new Set();
  // Once an element has finished revealing, drop the reveal hooks so its normal
  // hover styles (card lift, image zoom) work at full speed again.
  const settle = (el) => {
    const delay = parseFloat(el.style.getPropertyValue('--d')) || 0;
    setTimeout(() => { el.removeAttribute('data-anim'); el.style.removeProperty('--d'); el.classList.add('is-settled'); }, 1300 + delay);
  };
  const reveal = (el) => { el.classList.add('in', 'is-revealed'); pending.delete(el); if (io) io.unobserve(el); if (el.dataset.anim) settle(el); };
  function sweepPassed() {
    const limit = window.innerHeight * 0.94;
    pending.forEach((el) => { if (el.getBoundingClientRect().top < limit) reveal(el); });
  }

  let io;
  function observer() {
    if (io) return io;
    io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) reveal(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    return io;
  }

  function initReveal(scope = document) {
    const supported = 'IntersectionObserver' in window && !reduceMotion;
    ANIM_MAP.forEach(([sel, type, step]) => {
      // stagger within each parent
      const groups = new Map();
      $$(sel, scope).forEach((el) => {
        if (el.classList.contains('reveal') || el.dataset.anim) return;
        const list = groups.get(el.parentElement) || [];
        list.push(el);
        groups.set(el.parentElement, list);
      });
      groups.forEach((list) => list.forEach((el, i) => {
        el.dataset.anim = type;
        if (step) el.style.setProperty('--d', `${Math.min(i, 8) * step}ms`);
      }));
    });
    const targets = $$('.reveal:not(.is-revealed), [data-anim]:not(.in)', scope);
    if (!supported) { targets.forEach((el) => el.classList.add('in', 'is-revealed')); return; }
    targets.forEach((el) => { pending.add(el); observer().observe(el); });
    requestAnimationFrame(sweepPassed);
  }

  /* ---------- story numbers count up when they appear ---------- */
  function initCounters(scope = document) {
    const nums = $$('.stat-num', scope);
    if (!nums.length) return;
    const run = (el) => {
      const node = el.firstChild;
      if (!node || node.nodeType !== 3) return;
      const raw = node.textContent.trim();
      const target = parseFloat(raw.replace(/[^\d.]/g, ''));
      if (!target || reduceMotion) return;
      const sep = raw.includes(',') ? ',' : '';
      const suffix = raw.replace(/[\d.,\s]/g, '');
      const t0 = performance.now();
      const dur = 1600;
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / dur);
        const eased = 1 - Math.pow(1 - k, 3);
        const val = Math.round(target * eased);
        node.textContent = (sep ? val.toLocaleString('en-US') : String(val)) + suffix;
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    if (!('IntersectionObserver' in window)) return;
    const cio = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) { run(e.target); cio.unobserve(e.target); }
    }), { threshold: 0.6 });
    nums.forEach((n) => cio.observe(n));
  }

  /* ---------- scroll: header state + hero parallax ---------- */
  function initScroll() {
    const hero = $('.hero-section .hero-img') || $('.mh-banner-img');
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      body.classList.toggle('is-scrolled', y > 40);
      sweepPassed();
      if (hero && !reduceMotion && y < 1200) document.documentElement.style.setProperty('--hero-shift', (y * 0.18).toFixed(1));
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    window.addEventListener('hashchange', () => setTimeout(sweepPassed, 50));
    update();
  }

  /* ---------- pointer glow on icon buttons (design --mx/--my) + tap ripples ---------- */
  function initPointerFx() {
    document.addEventListener('pointermove', (e) => {
      const btn = e.target.closest && e.target.closest('.icon-btn');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      btn.style.setProperty('--mx', `${e.clientX - r.left}px`);
      btn.style.setProperty('--my', `${e.clientY - r.top}px`);
    }, { passive: true });

    if (reduceMotion) return;
    document.addEventListener('pointerdown', (e) => {
      const el = e.target.closest && e.target.closest('.btn-solid, .btn-pill, .faq-q, .icon-btn');
      if (!el) return;
      const r = el.getBoundingClientRect();
      const size = Math.max(r.width, r.height);
      const dot = document.createElement('span');
      dot.className = 'ripple';
      dot.style.width = dot.style.height = `${size}px`;
      dot.style.left = `${e.clientX - r.left - size / 2}px`;
      dot.style.top = `${e.clientY - r.top - size / 2}px`;
      el.appendChild(dot);
      setTimeout(() => dot.remove(), 650);
    }, { passive: true });
  }

  /* ---------- mobile menu ---------- */
  function initMenu() {
    const btn = $('[data-menu-toggle]');
    const menu = $('[data-mobile-menu]');
    const scrim = $('.mobile-menu-scrim');
    if (!btn || !menu) return;
    const set = (open) => {
      btn.classList.toggle('is-open', open);
      menu.classList.toggle('open', open);
      if (scrim) scrim.classList.toggle('show', open);
      btn.setAttribute('aria-expanded', String(open));
      btn.setAttribute('aria-label', open ? btn.dataset.labelClose : btn.dataset.labelOpen);
      menu.setAttribute('aria-hidden', String(!open));
      document.documentElement.classList.toggle('scroll-lock', open);
    };
    btn.addEventListener('click', () => set(!menu.classList.contains('open')));
    if (scrim) scrim.addEventListener('click', () => set(false));
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) set(false); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') set(false); });
  }

  /* ---------- nav tap animation (design) ---------- */
  function initNavTap() {
    const nav = $('[data-nav-tap]');
    if (!nav) return;
    nav.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      a.classList.remove('is-tapped');
      void a.offsetWidth;
      a.classList.add('is-tapped');
      setTimeout(() => a.classList.remove('is-tapped'), 520);
    });
  }

  /* ---------- language switcher ---------- */
  function initLang() {
    $$('[data-lang-switch]').forEach((wrap) => {
      const trigger = $('[data-lang-trigger]', wrap);
      const set = (open) => { wrap.classList.toggle('is-open', open); trigger.setAttribute('aria-expanded', String(open)); };
      trigger.addEventListener('click', () => set(!wrap.classList.contains('is-open')));
      document.addEventListener('mousedown', (e) => { if (!wrap.contains(e.target)) set(false); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') set(false); });
    });
  }

  /* ---------- hero hotspots (design: hover/focus opens, 180ms close delay) ---------- */
  function initHotspots(scope = document) {
    $$('.hero-wrap', scope).forEach((wrap) => {
      const spots = $$('[data-hotspot]', wrap);
      if (!spots.length) return;
      let timer = null;
      const pop = (id) => $(`[data-popover="${id}"]`, wrap);
      const closeAll = (except) => spots.forEach((s) => {
        if (s.dataset.hotspot === except) return;
        s.classList.remove('is-active');
        const p = pop(s.dataset.hotspot);
        if (p) p.classList.remove('open');
      });
      const open = (id) => {
        clearTimeout(timer);
        closeAll(id);
        const s = spots.find((x) => x.dataset.hotspot === id);
        if (s) s.classList.add('is-active');
        const p = pop(id);
        if (p) p.classList.add('open');
      };
      const scheduleClose = (id) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const s = spots.find((x) => x.dataset.hotspot === id);
          if (s) s.classList.remove('is-active');
          const p = pop(id);
          if (p) p.classList.remove('open');
        }, 180);
      };
      spots.forEach((s) => {
        const id = s.dataset.hotspot;
        s.addEventListener('mouseenter', () => open(id));
        s.addEventListener('mouseleave', () => scheduleClose(id));
        s.addEventListener('focus', () => open(id));
        s.addEventListener('blur', () => scheduleClose(id));
        s.addEventListener('click', (e) => {
          e.stopPropagation();
          const p = pop(id);
          if (!canHover && p && p.classList.contains('open')) scheduleClose(id);
          else open(id);
        });
        s.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(id); } });
        const p = pop(id);
        if (p) {
          p.addEventListener('mouseenter', () => open(id));
          p.addEventListener('mouseleave', () => scheduleClose(id));
          p.addEventListener('focusin', () => open(id));
          p.addEventListener('focusout', (e) => { if (!p.contains(e.relatedTarget)) scheduleClose(id); });
        }
      });
      document.addEventListener('click', (e) => { if (!e.target.closest('[data-hotspot], [data-popover]')) closeAll(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
    });
  }

  /* ---------- mobile product rail: dots, drag, centred-card focus ---------- */
  function initRails(scope = document) {
    $$('[data-rail]', scope).forEach((rail) => {
      if (rail.dataset.bound) return;
      rail.dataset.bound = '1';
      const dots = $$('[data-rail-dot]', rail.parentElement);
      const cards = () => $$(rail.dataset.railCard || '.m-prod-card', rail);
      const centred = () => {
        const mid = rail.scrollLeft + rail.clientWidth / 2;
        let best = 0, dist = Infinity;
        cards().forEach((c, i) => {
          const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid);
          if (d < dist) { dist = d; best = i; }
        });
        return best;
      };
      const update = () => {
        const i = centred();
        dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
        cards().forEach((c, k) => c.classList.toggle('is-centered', k === i));
        rail.classList.add('has-focus');
      };
      const goTo = (i) => { const c = cards()[i]; if (c) rail.scrollTo({ left: c.offsetLeft - (rail.clientWidth - c.offsetWidth) / 2, behavior: reduceMotion ? 'auto' : 'smooth' }); };
      let raf;
      rail.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
      dots.forEach((d, i) => d.addEventListener('click', () => goTo(i)));

      // pointer drag (mouse / trackpad); touch keeps native scrolling
      let down = false, startX = 0, startLeft = 0, moved = false;
      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch' || e.target.closest('button, a.btn-solid') || rail.scrollWidth <= rail.clientWidth + 1) return;
        down = true; moved = false; startX = e.clientX; startLeft = rail.scrollLeft;
        rail.classList.add('is-dragging');
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        rail.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        rail.classList.remove('is-dragging');
        goTo(centred());
      });
      rail.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      update();
    });
  }

  /* ---------- FAQ: one open at a time, +/− swap ---------- */
  function initFaq(scope = document) {
    $$('[data-faq]', scope).forEach((list) => {
      if (list.dataset.bound) return;
      list.dataset.bound = '1';
      list.addEventListener('click', (e) => {
        const q = e.target.closest('.faq-q');
        if (!q) return;
        const item = q.closest('.faq-item');
        const willOpen = !item.classList.contains('is-open');
        $$('.faq-item', list).forEach((it) => {
          const open = it === item && willOpen;
          it.classList.toggle('is-open', open);
          $('.faq-q', it).setAttribute('aria-expanded', String(open));
          $('.faq-icon', it).textContent = open ? '−' : '+';
          $('.faq-a', it).hidden = !open;
        });
      });
    });
  }

  /* ============================================================
     CART
     ============================================================ */
  const drawerId = 'cart-drawer';
  const drawerRoot = () => $('[data-cart-drawer]');

  function setDrawer(open) {
    const root = drawerRoot();
    if (!root) { if (open) window.location.href = S.routes.cart; return; }
    $('.scrim', root).classList.toggle('show', open);
    const d = $('.drawer', root);
    d.classList.toggle('open', open);
    d.setAttribute('aria-hidden', String(!open));
    // Lock scrolling on <html>: locking <body> breaks the sticky header (html has overflow-x: clip)
    document.documentElement.classList.toggle('scroll-lock', open);
    body.classList.toggle('drawer-open', open);
    if (open) setTimeout(() => d.focus({ preventScroll: true }), 60);
  }
  const isOpen = () => { const r = drawerRoot(); return !!(r && $('.drawer.open', r)); };

  function renderDrawer(html) {
    if (!html) return;
    const wasOpen = isOpen();
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]');
    const current = drawerRoot();
    if (!fresh || !current) return;
    if (wasOpen) {
      $('.scrim', fresh).classList.add('show');
      $('.drawer', fresh).classList.add('open');
    }
    current.replaceWith(fresh);
  }

  function updateCounts(cart) {
    $$('[data-cart-count]').forEach((pip) => {
      pip.textContent = cart.item_count;
      pip.hidden = cart.item_count === 0;
      pip.classList.remove('is-popping'); void pip.offsetWidth; pip.classList.add('is-popping');
    });
    const bar = $('[data-shopbar]');
    if (!bar) return;
    const has = cart.item_count > 0;
    bar.classList.toggle('has-items', has);
    $('[data-shopbar-full]', bar).hidden = !has;
    $('[data-shopbar-empty]', bar).hidden = has;
    $('[data-shopbar-cta-bag]', bar).hidden = !has;
    $('[data-shopbar-cta-shop]', bar).hidden = has;
    $('[data-shopbar-count]', bar).textContent = (bar.dataset.inBag || '[n] in bag').replace('[n]', cart.item_count);
    $('[data-shopbar-total]', bar).textContent = formatMoney(cart.total_price);
    bar.classList.remove('is-bumped'); void bar.offsetWidth; bar.classList.add('is-bumped');
  }

  async function fetchCart() {
    const r = await fetch(`${S.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    return r.json();
  }

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  /* Softly highlight the line(s) that were just added when the bag opens */
  function markAdded(ids) {
    const root = drawerRoot();
    if (!root) return;
    $$('.cart-item', root).forEach((row) => {
      const id = row.dataset.variantId;
      if (id && ids.includes(String(id))) row.classList.add('is-new');
    });
  }

  function flashAdded(btn) {
    if (!btn) return;
    if (!btn._html) btn._html = btn.innerHTML;
    clearTimeout(btn._t1); clearTimeout(btn._t2);
    btn.style.minWidth = btn.offsetWidth + 'px';
    const txt = ((S.strings && S.strings.added) || 'Added').replace(/s*✓s*$/, '');
    btn.classList.remove('is-added', 'is-resetting', 'added');
    void btn.offsetWidth;
    btn.classList.add('is-added');
    btn.innerHTML = `<span class="atc-done"><svg class="atc-check" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M3 8.6l3.1 3L13 4.6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg><span>${txt}</span></span>`;
    btn._t1 = setTimeout(() => {
      btn.classList.add('is-resetting');
      btn._t2 = setTimeout(() => { btn.classList.remove('is-added', 'is-resetting'); btn.innerHTML = btn._html; btn.style.minWidth = ''; }, 400);
    }, 2200);
  }

  function showError(btn, msg) {
    if (!btn) { console.error(msg); return; }
    let n = btn.parentElement.querySelector('.form-error');
    if (!n) { n = document.createElement('p'); n.className = 'form-error'; n.setAttribute('role', 'alert'); btn.insertAdjacentElement('afterend', n); }
    n.textContent = msg || S.strings.error;
    setTimeout(() => n.remove(), 5000);
  }

  async function addItems(items, btn) {
    if (btn) btn.setAttribute('aria-busy', 'true');
    try {
      const r = await fetch(`${S.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: [drawerId], sections_url: window.location.pathname })
      });
      const data = await r.json();
      if (!r.ok || data.status) throw new Error(data.description || data.message || S.strings.error);
      flashAdded(btn);
      const addedIds = items.map((i) => String(i.id));
      if (data.sections) renderDrawer(data.sections[drawerId]);
      updateCounts(await fetchCart());
      markAdded(addedIds);
      await wait(reduceMotion ? 0 : 420);
      if (S.cartType === 'drawer') setDrawer(true); else window.location.href = S.routes.cart;
    } catch (err) {
      showError(btn, err.message);
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }

  async function changeLine(key, quantity) {
    const d = $('.drawer', drawerRoot() || document);
    if (d) d.classList.add('is-loading');
    try {
      const r = await fetch(`${S.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [drawerId], sections_url: window.location.pathname })
      });
      const cart = await r.json();
      if (!r.ok || cart.status) throw new Error(cart.description);
      if (cart.sections) renderDrawer(cart.sections[drawerId]);
      updateCounts(cart);
    } catch (err) { console.error(err); }
  }

  async function clearCart() {
    const r = await fetch(`${S.routes.root.replace(/\/$/, '')}/cart/clear.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ sections: [drawerId], sections_url: window.location.pathname })
    });
    const cart = await r.json();
    if (cart.sections) renderDrawer(cart.sections[drawerId]);
    updateCounts(cart);
  }

  function initCart() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-add-to-cart]');
      if (!form) return;
      e.preventDefault();
      const fd = new FormData(form);
      // Bundle cards add each component product; the bundle app applies its discount in the cart
      const parts = fd.getAll('bundle_id').map(Number).filter(Boolean);
      if (parts.length) { addItems(parts.map((pid) => ({ id: pid, quantity: 1 })), e.submitter || $('[type="submit"]', form)); return; }
      const id = Number(fd.get('id'));
      if (!id) return;
      addItems([{ id, quantity: Number(fd.get('quantity') || 1) }], e.submitter || $('[type="submit"]', form));
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-open]') && S.cartType === 'drawer' && drawerRoot()) { e.preventDefault(); setDrawer(true); return; }
      if (e.target.closest('[data-cart-close]')) { setDrawer(false); return; }
      if (e.target.closest('[data-cart-clear]')) { clearCart(); return; }
      const line = e.target.closest('[data-line-change]');
      if (line) changeLine(line.dataset.lineChange, Math.max(0, Number(line.dataset.qty)));
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen()) setDrawer(false); });
  }

  /* ============================================================
     PRODUCT PAGE
     ============================================================ */
  function initProduct(scope = document) {
    $$('[data-product-section]', scope).forEach((section) => {
      $$('[data-qty]', section).forEach((wrap) => {
        const input = $('input', wrap);
        $$('[data-qty-step]', wrap).forEach((b) => b.addEventListener('click', () => {
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.qtyStep));
        }));
      });
      const slides = $$('.pdp__slide', section);
      const thumbs = $$('[data-thumb]', section);
      const show = (id) => {
        slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === String(id)));
        thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === String(id)));
      };
      thumbs.forEach((t) => t.addEventListener('click', () => show(t.dataset.thumb)));

      const json = $('[data-variants]', section);
      if (!json) return;
      const variants = JSON.parse(json.textContent);
      const idInput = $('[data-variant-id]', section);
      const priceEl = $('[data-price]', section);
      const addBtn = $('[data-add-button]', section);
      $$('[data-option-input]', section).forEach((inp) => inp.addEventListener('change', () => {
        const sel = [1, 2, 3].map((n) => { const c = section.querySelector(`[name="option${n}"]:checked`); return c ? c.value : null; }).filter((v) => v !== null);
        const v = variants.find((x) => x.options.every((o, i) => o === sel[i]));
        if (!v) { addBtn.disabled = true; return; }
        idInput.value = v.id;
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? S.strings.addToBag : S.strings.soldOut;
        if (priceEl) priceEl.innerHTML = `${v.compare_at_price > v.price ? `<s class="price-was">${formatMoney(v.compare_at_price)}</s>` : ''}<span class="price-now">${formatMoney(v.price)}</span>`;
        if (v.featured_media) show(v.featured_media.id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url);
      }));
    });
  }

  /* ---------- frequently bought together ---------- */
  function bindFbt(section) {
    const box = $('[data-fbt]', section);
    if (!box || box.dataset.bound) return;
    box.dataset.bound = '1';
    const inputs = $$('[data-fbt-input]', box);
    const total = $('[data-fbt-total]', box);
    const add = $('[data-fbt-add]', box);
    const recalc = () => {
      let sum = 0, n = 0;
      inputs.forEach((i) => { i.closest('.fbt__item').classList.toggle('is-off', !i.checked); if (i.checked) { sum += Number(i.dataset.price); n++; } });
      total.textContent = formatMoney(sum);
      add.disabled = n === 0;
    };
    inputs.forEach((i) => i.addEventListener('change', recalc));
    add.addEventListener('click', () => {
      const items = inputs.filter((i) => i.checked).map((i) => ({ id: Number(i.value), quantity: 1 }));
      if (items.length) addItems(items, add);
    });
    recalc();
  }

  async function initRecommendations(scope = document) {
    for (const section of $$('[data-recommendations]', scope)) {
      if (section.dataset.loaded === 'true') { bindFbt(section); continue; }
      const load = async (url) => {
        const html = await (await fetch(url)).text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        return fresh && fresh.querySelector('[data-fbt]') ? fresh : null;
      };
      try {
        const fresh = (await load(section.dataset.url)) || (await load(section.dataset.fallbackUrl));
        if (fresh) { section.innerHTML = fresh.innerHTML; bindFbt(section); initReveal(section); }
        section.dataset.loaded = 'true';
      } catch (err) { console.error(err); }
    }
  }

  /* ============================================================
     COOKIE CONSENT (Shopify Customer Privacy API)
     ============================================================ */
  function initCookies() {
    const banner = $('[data-cookie-banner]');
    if (!banner) return;
    const prefs = $('[data-cookie-prefs]', banner);
    const btn = (n) => $(`[data-cookie-action="${n}"]`, banner);
    const show = (withPrefs) => {
      banner.hidden = false; body.classList.add('cookie-visible');
      prefs.hidden = !withPrefs; btn('customize').hidden = withPrefs; btn('save').hidden = !withPrefs;
    };
    const hide = () => { banner.hidden = true; body.classList.remove('cookie-visible'); };
    const api = () => window.Shopify && window.Shopify.customerPrivacy;
    const apply = (c) => {
      if (!api()) { hide(); return; }
      api().setTrackingConsent({ analytics: c.analytics, marketing: c.marketing, preferences: c.preferences, sale_of_data: c.marketing }, hide);
    };
    const read = () => { const v = (k) => { const el = $(`[data-consent="${k}"]`, banner); return !!(el && el.checked); }; return { analytics: v('analytics'), marketing: v('marketing'), preferences: v('preferences') }; };
    btn('accept').addEventListener('click', () => apply({ analytics: true, marketing: true, preferences: true }));
    btn('decline').addEventListener('click', () => apply({ analytics: false, marketing: false, preferences: false }));
    btn('customize').addEventListener('click', () => show(true));
    btn('save').addEventListener('click', () => apply(read()));
    $$('[data-cookie-reopen]').forEach((el) => el.addEventListener('click', () => {
      const a = api();
      if (a) {
        $('[data-consent="analytics"]', banner).checked = a.analyticsProcessingAllowed();
        $('[data-consent="marketing"]', banner).checked = a.marketingAllowed();
        if (a.preferencesProcessingAllowed) $('[data-consent="preferences"]', banner).checked = a.preferencesProcessingAllowed();
      }
      show(true);
    }));
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
        if (err || !api()) return;
        const undecided = !api().getTrackingConsent || api().getTrackingConsent() === 'no_interaction';
        if (api().shouldShowBanner() && undecided) show(false);
      });
    }
  }

  /* ============================================================
     BOOT + THEME EDITOR
     ============================================================ */
  function init() {
    initReveal();
    initCounters();
    initScroll();
    initPointerFx();
    initMenu();
    initNavTap();
    initLang();
    initHotspots();
    initRails();
    initFaq();
    initCart();
    initProduct();
    initRecommendations();
    initCookies();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target); initCounters(e.target); initHotspots(e.target); initRails(e.target);
    initFaq(e.target); initProduct(e.target); initRecommendations(e.target);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const id = e.target.dataset && e.target.dataset.hotspot;
    if (id) { const p = document.querySelector(`[data-popover="${id}"]`); if (p) p.classList.add('open'); }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const id = e.target.dataset && e.target.dataset.hotspot;
    if (id) { const p = document.querySelector(`[data-popover="${id}"]`); if (p) p.classList.remove('open'); }
  });
})();
